import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { rx, doses as dosesApi, downloadFile, API, google, reactions as reactionsApi, adherenceSummary, caregivers as caregiversApi, digest as digestApi } from "../lib/api";
import { getRxAlarmOverride, setRxAlarmOverride, resolveAlarm, playAlarm, stopAlarm, TONE_LIST } from "../lib/alarm";
import { firePopup } from "../lib/reminders";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Calendar } from "../components/ui/calendar";
import { ArrowLeft, CalendarDays, Download, FileDown, Printer, CheckCircle2, Circle, XCircle, Clock, Info, Flame, Share2, Link2Off, Copy, Heart, Bell, Users, Mail, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { getClientId } from "../lib/api";
import TaperingPanel from "../components/TaperingPanel";
import NextDoseChangeCard from "../components/NextDoseChangeCard";
import FamilyShareToggle from "../components/FamilyShareToggle";
import AuditFeed from "../components/AuditFeed";

function dateKey(d) {
  const dt = new Date(d);
  return dt.toISOString().slice(0, 10);
}

function sameDay(a, b) {
  return dateKey(a) === dateKey(b);
}

function doseClass(dose) {
  if (dose.status === "taken") return "taken";
  if (dose.status === "auto_taken") return "auto-taken";
  if (dose.status === "skipped") return "skipped";
  const diff = (new Date(dose.scheduled_at).getTime() - Date.now()) / 60000;
  if (diff > 0 && diff < 60) return "due-soon";
  return "pending";
}

const statusIcon = (s) => {
  if (s === "taken" || s === "auto_taken") return <CheckCircle2 className="h-4 w-4 text-[color:var(--success)]" />;
  if (s === "skipped") return <XCircle className="h-4 w-4 text-[color:var(--accent-ink)]" />;
  return <Circle className="h-4 w-4 text-[color:var(--text-muted)]" />;
};

function Sparkline({ data }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  const w = 70, h = 18, pad = 1;
  const max = Math.max(1, ...data);
  const stepX = (w - pad * 2) / Math.max(1, data.length - 1);
  const points = data.map((v, i) => {
    const x = pad + stepX * i;
    const y = h - pad - (v / max) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const total = data.reduce((a, b) => a + b, 0);
  return (
    <span className="inline-flex items-center gap-1" data-testid="sparkline" title={`7-day opens: ${total}`}>
      <span className="label-caps" style={{ fontSize: "10px" }}>7d opens</span>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
        <polyline fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
        {data.map((v, i) => {
          if (v === 0) return null;
          const x = pad + stepX * i;
          const y = h - pad - (v / max) * (h - pad * 2);
          return <circle key={i} cx={x} cy={y} r="1.5" fill="var(--primary)" />;
        })}
      </svg>
    </span>
  );
}

export default function PrescriptionDetailPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [rxData, setRxData] = useState(null);
  const [doses, setDoses] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [openDose, setOpenDose] = useState(null);
  const [view, setView] = useState("calendar"); // calendar | agenda
  const [gStatus, setGStatus] = useState({ configured: false, connected: false });
  const [pushingGoogle, setPushingGoogle] = useState(false);
  const [adherence, setAdherence] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [reactionsFeed, setReactionsFeed] = useState([]);
  const [cgList, setCgList] = useState([]);
  const [digestStats, setDigestStats] = useState(null);
  const [alarmOverride, setAlarmOverrideState] = useState(() => getRxAlarmOverride(id));
  const effectiveAlarm = resolveAlarm(id);

  function patchOverride(patch) {
    const next = patch === null ? null : { ...(alarmOverride || {}), ...patch };
    setRxAlarmOverride(id, next);
    setAlarmOverrideState(next);
  }

  async function load() {
    try {
      const d = await rx.get(id);
      setRxData(d.prescription);
      setDoses(d.doses);
      setShareToken(d.prescription.share_token || null);
      try {
        const a = await rx.adherence(id);
        setAdherence(a);
      } catch { /* ignore */ }
      try {
        const rlist = await reactionsApi.list(id);
        setReactionsFeed(rlist || []);
      } catch { /* ignore */ }
      try {
        const cl = await caregiversApi.list(id);
        setCgList(cl || []);
      } catch { /* ignore */ }
      try {
        const ds = await digestApi.stats(id);
        setDigestStats(ds);
      } catch { /* digests may not be configured */ }
    } catch (e) {
      toast.error("Failed to load schedule");
    }
  }

  useEffect(() => { load(); }, [id]);

  // Ring-on-open: service worker routes dose notifications to ?due=<dose_id>.
  // When the tap happens, rxData loads, then we find the dose and fire the popup (which plays the alarm).
  useEffect(() => {
    const due = params.get("due");
    if (!due || !rxData || !doses.length) return;
    if (effectiveAlarm.ringOnOpen) {
      const dose = doses.find(d => d.id === due && d.status === "pending");
      if (dose) firePopup({ prescription: rxData, dose });
    }
    params.delete("due");
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rxData, doses]);
  useEffect(() => { google.status().then(setGStatus).catch(() => {}); }, []);

  async function pushToGoogle() {
    setPushingGoogle(true);
    try {
      const res = await google.pushPrescription(id, false);
      toast.success(`Pushed ${res.inserted} dose${res.inserted === 1 ? "" : "s"} to Google Calendar${res.skipped ? ` (${res.skipped} skipped)` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Google push failed");
    } finally {
      setPushingGoogle(false);
    }
  }

  async function createShare() {
    try {
      const res = await rx.share(id);
      setShareToken(res.token);
      const url = `${window.location.origin}/share/${res.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard"); }
      catch { toast.success("Share link created"); }
    } catch { toast.error("Could not create share link"); }
  }

  async function revokeShare() {
    if (!window.confirm("Revoke the share link? Anyone holding it will lose access.")) return;
    try {
      await rx.revokeShare(id);
      setShareToken(null);
      toast.success("Share link revoked");
    } catch { toast.error("Could not revoke"); }
  }

  function copyShare() {
    if (!shareToken) return;
    const url = `${window.location.origin}/share/${shareToken}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Copied to clipboard")).catch(() => toast.error("Copy failed"));
  }

  async function sendSummary() {
    try {
      const res = await adherenceSummary.send();
      if (res.sent > 0) {
        toast.success("Adherence summary pushed to your devices");
      } else if (res.reason === "no past doses" || res.reason === "no prescriptions") {
        toast.message(res.reason === "no past doses" ? "Nothing to summarise yet — record some doses first." : "No prescriptions to summarise.");
      } else {
        // sent === 0 with no clear reason → user hasn't enabled Background Push
        toast.warning("Background Push isn't enabled yet.", {
          description: "Open Settings and toggle Background Push on, then try again.",
          action: {
            label: "Open Settings",
            onClick: () => {
              const el = document.querySelector('[data-testid="push-toggle"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            },
          },
        });
      }
    } catch { toast.error("Could not send summary"); }
  }

  async function revokeCaregiver(subId, name) {
    if (!window.confirm(`Stop sending reminders to ${name}?`)) return;
    try {
      await caregiversApi.remove(id, subId);
      toast.success("Caregiver removed");
      setCgList(cgList.filter(c => c.id !== subId));
    } catch { toast.error("Could not remove"); }
  }

  const dayDoses = useMemo(() => doses.filter(d => sameDay(d.scheduled_at, selectedDate)).sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)), [doses, selectedDate]);

  const modifiers = useMemo(() => {
    const map = { hasTaken: [], hasPending: [], hasSkipped: [] };
    for (const d of doses) {
      const dt = new Date(d.scheduled_at);
      const day = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
      if (d.status === "taken" || d.status === "auto_taken") map.hasTaken.push(day);
      else if (d.status === "skipped") map.hasSkipped.push(day);
      else map.hasPending.push(day);
    }
    return map;
  }, [doses]);

  async function mark(dose, status) {
    try {
      await dosesApi.update(dose.id, status);
      toast.success(`Marked ${status.replace("_", " ")}`);
      setOpenDose(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Failed to update dose");
    }
  }

  // A dose is "actionable as taken" only when it's not too far in the future
  // (10 min grace matches the backend guard). Past doses + today's doses are
  // always actionable.
  function isTooEarlyToTake(dose) {
    if (!dose) return false;
    const sched = new Date(dose.scheduled_at).getTime();
    return sched - Date.now() > 10 * 60 * 1000;
  }

  // Friendly relative-day prefix used in the dose dialog. Returns "Take today",
  // "Take tomorrow", "Take Monday", "Past dose", etc.
  function doseDayLabel(dose) {
    if (!dose) return "";
    const sched = new Date(dose.scheduled_at);
    const now = new Date();
    const dayDiff = Math.round(
      (new Date(sched.getFullYear(), sched.getMonth(), sched.getDate()).getTime() -
       new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 86400000
    );
    if (dose.status === "taken" || dose.status === "auto_taken") return "Past dose";
    if (dayDiff < 0) return "Past dose";
    if (dayDiff === 0) return "Take today";
    if (dayDiff === 1) return "Take tomorrow";
    if (dayDiff < 7) return `Take ${sched.toLocaleDateString([], { weekday: "long" })}`;
    return `Upcoming · ${sched.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}`;
  }

  function googleCalUrl(dose) {
    if (!rxData) return "#";
    const dt = new Date(dose.scheduled_at);
    const end = new Date(dt.getTime() + 15 * 60000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const text = `Take ${rxData.medicine_name} — ${dose.dose_label || rxData.dosage}`;
    const details = `${rxData.instructions || ""} (via Handy)`;
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(text)}&dates=${fmt(dt)}/${fmt(end)}&details=${encodeURIComponent(details)}`;
  }

  // Today's pending doses (used by the "Mark all today's doses taken" shortcut).
  // Grace + future-guard match the backend bulk endpoint so the button only
  // shows when there's actually something to mark.
  const todaysPendingDoses = useMemo(() => {
    const now = new Date();
    const startLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const endLocal = startLocal + 86400000;
    const cutoff = Date.now() + 10 * 60 * 1000;
    return (doses || []).filter(d => {
      if (d.status !== "pending") return false;
      const t = new Date(d.scheduled_at).getTime();
      return t >= startLocal && t < endLocal && t <= cutoff;
    });
  }, [doses]);

  const [bulkBusy, setBulkBusy] = useState(false);
  async function bulkTakeToday() {
    if (todaysPendingDoses.length === 0) return;
    if (!window.confirm(`Mark all ${todaysPendingDoses.length} of today's pending dose(s) as taken?`)) return;
    setBulkBusy(true);
    try {
      const now = new Date();
      const startLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endLocal = new Date(startLocal.getTime() + 86400000);
      const r = await dosesApi.bulkTakeToday(id, startLocal.toISOString(), endLocal.toISOString());
      toast.success(`Marked ${r.marked} dose(s) as taken`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark doses");
    } finally { setBulkBusy(false); }
  }

  const safeName = rxData ? rxData.medicine_name.replace(/[^a-z0-9]+/gi, "_") : "prescription";

  return (
    <div className="animate-fade-up">
      <Link to="/prescriptions" className="inline-flex items-center gap-2 text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] mb-6" data-testid="rx-back">
        <ArrowLeft className="h-4 w-4" /> All prescriptions
      </Link>

      {!rxData ? (
        <div className="text-[color:var(--text-muted)]">Loading…</div>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
            <div>
              <span className="label-caps">Course</span>
              <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">{rxData.medicine_name}</h1>
              <p className="mt-2 text-[color:var(--text-muted)]">
                {rxData.tapering_schedule && rxData.tapering_schedule.length > 1
                  ? `${rxData.tapering_schedule[0].dose_amount} → ${rxData.tapering_schedule[rxData.tapering_schedule.length - 1].dose_amount} · tapering`
                  : rxData.dosage}
                {" · "}{rxData.frequency_per_day}x/day · {rxData.duration_days} days · starts {new Date(rxData.start_datetime).toLocaleString()}
              </p>
              {(() => {
                // Today's per-day chip, only when tapering is active
                const bands = rxData.tapering_schedule;
                if (!bands || bands.length === 0) return null;
                const today = (doses || []).find(d => {
                  const s = new Date(d.scheduled_at);
                  const now = new Date();
                  return s.toDateString() === now.toDateString();
                });
                if (!today || !today.dose_label) return null;
                return (
                  <div
                    data-testid="header-today-chip"
                    className="inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full"
                    style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}
                  >
                    <span className="text-[10px] uppercase tracking-wider font-bold">Today</span>
                    <span className="text-sm font-semibold">{today.dose_label}</span>
                    {today.day_offset && (
                      <span className="text-[11px] opacity-70">· Day {today.day_offset}{today.week_number ? ` · Wk ${today.week_number}` : ""}</span>
                    )}
                  </div>
                );
              })()}
              {rxData.instructions && <p className="text-sm text-[color:var(--text-muted)] mt-1"><Info className="h-3 w-3 inline mr-1" />{rxData.instructions}</p>}
              {todaysPendingDoses.length > 0 && (
                <button
                  onClick={bulkTakeToday}
                  disabled={bulkBusy}
                  data-testid="bulk-take-today"
                  className="mt-3 inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "var(--primary)" }}
                  title="Useful when you take everything from a weekly pill organiser at once"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Mark all today's {todaysPendingDoses.length === 1 ? "dose" : `${todaysPendingDoses.length} doses`} taken
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-lg border border-[color:var(--border-subtle)] overflow-hidden" role="tablist">
                <button data-testid="view-calendar" onClick={() => setView("calendar")} className={`px-3 py-2 text-sm font-semibold ${view === "calendar" ? "bg-[color:var(--primary)] text-white" : "bg-[color:var(--surface)] text-[color:var(--text-main)]"}`}>Calendar</button>
                <button data-testid="view-agenda" onClick={() => setView("agenda")} className={`px-3 py-2 text-sm font-semibold ${view === "agenda" ? "bg-[color:var(--primary)] text-white" : "bg-[color:var(--surface)] text-[color:var(--text-main)]"}`}>Agenda</button>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="export-menu" className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white rounded-lg h-11">
                    <Download className="h-4 w-4 mr-2" /> Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid="export-ics" onClick={() => downloadFile(rx.icsUrl(id), `${safeName}.ics`)}>
                    <CalendarDays className="h-4 w-4 mr-2" /> Download .ics (Apple/Samsung/Outlook)
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="export-google" onClick={() => {
                    const first = doses.find(d => d.status === "pending") || doses[0];
                    if (first) window.open(googleCalUrl(first), "_blank");
                  }}>
                    <CalendarDays className="h-4 w-4 mr-2" /> Add next dose to Google Calendar
                  </DropdownMenuItem>
                  {gStatus.configured && gStatus.connected && (
                    <DropdownMenuItem data-testid="export-google-push" onClick={pushToGoogle} disabled={pushingGoogle}>
                      <CalendarDays className="h-4 w-4 mr-2" /> {pushingGoogle ? "Pushing…" : "Push full schedule to Google Calendar"}
                    </DropdownMenuItem>
                  )}
                  {gStatus.configured && !gStatus.connected && (
                    <DropdownMenuItem data-testid="export-google-connect" asChild>
                      <Link to="/settings"><CalendarDays className="h-4 w-4 mr-2" /> Connect Google Calendar…</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="export-pdf" onClick={() => downloadFile(rx.pdfUrl(id), `${safeName}.pdf`)}>
                    <FileDown className="h-4 w-4 mr-2" /> Printable PDF (with QR)
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="export-csv" onClick={() => downloadFile(rx.activityCsvUrl(id), `${safeName}_activity.csv`)}>
                    <FileDown className="h-4 w-4 mr-2" /> Activity CSV (for care provider)
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="export-print" onClick={() => window.print()}>
                    <Printer className="h-4 w-4 mr-2" /> Print this page
                  </DropdownMenuItem>
                  {shareToken ? (
                    <>
                      <DropdownMenuItem data-testid="share-copy" onClick={copyShare}>
                        <Copy className="h-4 w-4 mr-2" /> Copy caregiver link
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="share-revoke" onClick={revokeShare}>
                        <Link2Off className="h-4 w-4 mr-2" /> Revoke caregiver link
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem data-testid="share-create" onClick={createShare}>
                      <Share2 className="h-4 w-4 mr-2" /> Create caregiver share link
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <NextDoseChangeCard rx={rxData} />

          <FamilyShareToggle rx={rxData} onChanged={() => load()} />

          <TaperingPanel rx={rxData} onUpdated={() => load()} />

          {/* Adherence panel */}
          {adherence && adherence.total_past > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="adherence-panel">
              <div className="card-tactile rounded-xl p-5">
                <span className="label-caps">Adherence</span>
                <div className="font-display font-black text-3xl mt-1 text-[color:var(--primary-ink)]">{adherence.adherence_pct}<span className="text-base text-[color:var(--text-muted)]">%</span></div>
                <div className="text-xs text-[color:var(--text-muted)] mt-1">{adherence.taken}/{adherence.total_past} past doses</div>
              </div>
              <div className="card-tactile rounded-xl p-5">
                <span className="label-caps flex items-center gap-1"><Flame className="h-3 w-3 text-[color:var(--accent-ink)]" /> Current streak</span>
                <div className="font-display font-black text-3xl mt-1">{adherence.current_streak}</div>
                <div className="text-xs text-[color:var(--text-muted)] mt-1">consecutive taken</div>
              </div>
              <div className="card-tactile rounded-xl p-5">
                <span className="label-caps">Longest streak</span>
                <div className="font-display font-black text-3xl mt-1">{adherence.longest_streak}</div>
                <div className="text-xs text-[color:var(--text-muted)] mt-1">personal best</div>
              </div>
              <div className="card-tactile rounded-xl p-5">
                <span className="label-caps">Missed / skipped</span>
                <div className="font-display font-black text-3xl mt-1 text-[color:var(--accent-ink)]">{adherence.missed + adherence.skipped}</div>
                <div className="text-xs text-[color:var(--text-muted)] mt-1">{adherence.missed} missed · {adherence.skipped} skipped</div>
              </div>
            </div>
          )}
          {adherence && adherence.total_past > 0 && (
            <div className="mb-6 flex justify-end">
              <Button variant="outline" size="sm" onClick={sendSummary} data-testid="send-summary">
                <Bell className="h-3 w-3 mr-1" /> Push adherence summary now
              </Button>
            </div>
          )}

          {/* Reactions feed */}
          {reactionsFeed.length > 0 && (
            <div className="card-tactile rounded-2xl p-5 mb-6" data-testid="reactions-feed">
              <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2 mb-3">
                <Heart className="h-4 w-4 text-[color:var(--accent-ink)]" /> Cheers from caregivers
              </h3>
              <ul className="space-y-1 text-sm">
                {reactionsFeed.slice(0, 8).map(r => (
                  <li key={r.id} className="flex items-center gap-2">
                    <span className="text-base">{r.emoji}</span>
                    <span className="font-semibold">{r.from_name}</span>
                    <span className="text-[color:var(--text-muted)]">· {new Date(r.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Family activity audit log */}
          <div className="mb-6">
            <AuditFeed rxId={rxData.id} />
          </div>

          {/* Subscribed caregivers */}
          {cgList.length > 0 && (
            <div className="card-tactile rounded-2xl p-5 mb-6" data-testid="caregivers-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
                  <Users className="h-4 w-4 text-[color:var(--primary-ink)]" /> Subscribed caregivers
                </h3>
                <span className="label-caps">{cgList.length}</span>
              </div>
              <ul className="divide-y divide-[color:var(--border-subtle)]">
                {cgList.map(c => (
                  <li key={c.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`caregiver-${c.id}`}>
                    <div className="h-8 w-8 rounded-full bg-[color:var(--primary-soft)] flex items-center justify-center text-[color:var(--primary-ink)] font-bold">
                      {c.caregiver_name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{c.caregiver_name}</div>
                      <div className="text-xs text-[color:var(--text-muted)]">subscribed {new Date(c.created_at).toLocaleDateString()}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => revokeCaregiver(c.id, c.caregiver_name)} data-testid={`caregiver-revoke-${c.id}`} className="border-[color:var(--border-subtle)]">
                      <Link2Off className="h-3 w-3 mr-1" /> Remove
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Active share banner */}
          {shareToken && (
            <div className="card-tactile rounded-2xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="share-banner">
              <div className="h-9 w-9 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
                <Share2 className="h-4 w-4 text-[color:var(--primary-ink)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">Caregiver share is active</div>
                <div className="text-xs text-[color:var(--text-muted)] truncate" data-testid="share-url">{`${window.location.origin}/share/${shareToken}`}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copyShare} data-testid="share-banner-copy" className="border-[color:var(--border-subtle)]"><Copy className="h-3 w-3 mr-1" /> Copy</Button>
                <Button size="sm" variant="outline" onClick={revokeShare} data-testid="share-banner-revoke" className="border-[color:var(--border-subtle)]"><Link2Off className="h-3 w-3 mr-1" /> Revoke</Button>
              </div>
            </div>
          )}

          {/* Digest delivery stats — visible when at least one caregiver is subscribed */}
          {digestStats && digestStats.subscribers > 0 && (
            <div className="card-tactile rounded-2xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="digest-stats-badge">
              <div className="h-9 w-9 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
                <Mail className="h-4 w-4 text-[color:var(--primary-ink)]" />
              </div>
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-semibold">
                  {digestStats.subscribers} caregiver{digestStats.subscribers === 1 ? "" : "s"} subscribed to weekly digest
                </div>
                {digestStats.webhook_configured ? (
                  <div className="text-xs text-[color:var(--text-muted)] mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="digest-stats-line">
                    <span>Last 30 days:{" "}
                      <b data-testid="digest-stat-delivered">{digestStats.last_30d.delivered}</b>
                      {" / "}<b>{digestStats.last_30d.sent || digestStats.last_30d.delivered}</b> delivered
                      {" · "}<b data-testid="digest-stat-opened">{digestStats.last_30d.opened}</b> opened
                      {digestStats.last_30d.bounced > 0 && <> {" · "}<b className="text-[color:var(--accent-ink)]">{digestStats.last_30d.bounced}</b> bounced</>}
                      {digestStats.last_30d.complained > 0 && <> {" · "}<b className="text-[color:var(--accent-ink)]">{digestStats.last_30d.complained}</b> complained</>}
                    </span>
                    {Array.isArray(digestStats.opens_7d_daily) && digestStats.opens_7d_daily.some(v => v > 0) && (
                      <Sparkline data={digestStats.opens_7d_daily} />
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-[color:var(--text-muted)] mt-0.5">Delivery analytics will appear once the Resend webhook is enabled.</div>
                )}
              </div>
            </div>
          )}

          {/* Alarm ring — per-Rx override */}
          <div className="card-tactile rounded-2xl p-5 mb-6" data-testid="alarm-override-card">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-display font-bold text-base tracking-tight flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-[color:var(--primary-ink)]" /> Alarm ring for this prescription
                </h3>
                <p className="text-sm mt-1 text-[color:var(--text-muted)]">
                  {alarmOverride
                    ? <>Using override: <b>{effectiveAlarm.tone}</b> · volume {Math.round(effectiveAlarm.volume * 100)}%</>
                    : <>Using global default: <b>{effectiveAlarm.tone}</b> · volume {Math.round(effectiveAlarm.volume * 100)}% · <Link to="/prescriptions" className="underline">edit global</Link></>}
                </p>
              </div>
              {alarmOverride ? (
                <Button size="sm" variant="outline" onClick={() => patchOverride(null)} data-testid="alarm-override-reset" className="border-[color:var(--border-subtle)]">
                  Reset to global
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => patchOverride({ tone: effectiveAlarm.tone, volume: effectiveAlarm.volume, enabled: effectiveAlarm.enabled })} data-testid="alarm-override-create" className="border-[color:var(--border-subtle)]">
                  Customize
                </Button>
              )}
            </div>
            {alarmOverride && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label htmlFor="alarm-override-enabled" className="text-sm">Enable alarm for this Rx</label>
                  <input
                    id="alarm-override-enabled"
                    type="checkbox"
                    checked={alarmOverride.enabled !== false}
                    onChange={(e) => patchOverride({ enabled: e.target.checked })}
                    data-testid="alarm-override-enabled"
                    className="h-4 w-4 accent-[color:var(--primary)]"
                  />
                </div>
                {alarmOverride.enabled !== false && (
                  <>
                    <div>
                      <label className="label-caps">Tone</label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {TONE_LIST.map(t => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => patchOverride({ tone: t.id })}
                            data-testid={`alarm-override-tone-${t.id}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${alarmOverride.tone === t.id ? "bg-[color:var(--primary)] text-white border-transparent" : "bg-transparent text-[color:var(--text-main)] border-[color:var(--border-subtle)] hover:border-[color:var(--primary)]"}`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="label-caps">Volume</label>
                      <input
                        type="range" min="0" max="1" step="0.05"
                        value={alarmOverride.volume ?? effectiveAlarm.volume}
                        onChange={(e) => patchOverride({ volume: Number(e.target.value) })}
                        data-testid="alarm-override-volume"
                        className="mt-1 w-full accent-[color:var(--primary)]"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Button onClick={() => playAlarm(id, { preview: true })} variant="outline" size="sm" data-testid="alarm-override-preview" className="border-[color:var(--border-subtle)]">
                        <Volume2 className="h-3 w-3 mr-1" /> Preview
                      </Button>
                      <Button onClick={stopAlarm} variant="ghost" size="sm" className="text-[color:var(--text-muted)]">Stop</Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {view === "calendar" ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="card-tactile rounded-2xl p-4 lg:col-span-1">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => d && setSelectedDate(d)}
                  modifiers={modifiers}
                  modifiersClassNames={{
                    hasTaken: "rdp-has-taken",
                    hasPending: "rdp-has-pending",
                    hasSkipped: "rdp-has-skipped",
                  }}
                  className="mx-auto"
                  data-testid="rx-calendar"
                />
                <style>{`
                  .rdp-has-taken { position: relative; }
                  .rdp-has-taken::after { content: ""; position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); width: 6px; height: 6px; border-radius: 999px; background: var(--success); }
                  .rdp-has-pending::after { content: ""; position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); width: 6px; height: 6px; border-radius: 999px; background: var(--warning); }
                  .rdp-has-skipped::after { content: ""; position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); width: 6px; height: 6px; border-radius: 999px; background: var(--accent); }
                `}</style>
              </div>

              <div className="card-tactile rounded-2xl p-6 lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-bold text-lg tracking-tight">{selectedDate.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</h3>
                  <span className="label-caps">{dayDoses.length} dose{dayDoses.length === 1 ? "" : "s"}</span>
                </div>
                {dayDoses.length === 0 ? (
                  <div className="text-sm text-[color:var(--text-muted)]">No doses scheduled for this day.</div>
                ) : (
                  <ul className="divide-y divide-[color:var(--border-subtle)]">
                    {dayDoses.map((d, i) => (
                      <li key={d.id} className="py-3 flex items-center justify-between gap-3 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                        <div className="flex items-center gap-3">
                          {statusIcon(d.status)}
                          <div>
                            <div className="font-semibold flex items-center gap-1">
                              {new Date(d.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              {d.pre_setup && <span title="Before course was added" className="text-[color:var(--text-muted)]">*</span>}
                            </div>
                            <div className="text-xs text-[color:var(--text-muted)]">{d.dose_label || rxData.dosage}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`dose-pill ${doseClass(d)}`}>
                            {d.status === "auto_taken" ? "Taken*" : d.status.replace("_", " ")}
                          </span>
                          <Button variant="outline" size="sm" onClick={() => setOpenDose({ dose: d, rx: rxData })} data-testid={`open-dose-${d.id}`} className="h-8 border-[color:var(--border-subtle)]">
                            Details
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <div className="card-tactile rounded-2xl p-6">
              <ul className="divide-y divide-[color:var(--border-subtle)]">
                {doses.map((d, i) => (
                  <li key={d.id} className="py-3 flex items-center justify-between gap-3 animate-fade-up" style={{ animationDelay: `${i * 20}ms` }}>
                    <div className="flex items-center gap-3">
                      {statusIcon(d.status)}
                      <div>
                        <div className="font-semibold flex items-center gap-1">
                          {new Date(d.scheduled_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          {d.pre_setup && <span title="Before course was added" className="text-[color:var(--text-muted)]">*</span>}
                        </div>
                        <div className="text-xs text-[color:var(--text-muted)]">{rxData.medicine_name} · {d.dose_label || rxData.dosage}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`dose-pill ${doseClass(d)}`}>
                        {d.status === "auto_taken" ? "Taken*" : d.status.replace("_", " ")}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => setOpenDose({ dose: d, rx: rxData })} data-testid={`agenda-open-${d.id}`} className="h-8 border-[color:var(--border-subtle)]">Details</Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Dialog open={!!openDose} onOpenChange={(o) => !o && setOpenDose(null)}>
            <DialogContent className="max-w-md" data-testid="dose-dialog">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">
                  {openDose?.rx.medicine_name}
                </DialogTitle>
              </DialogHeader>
              {openDose && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-[color:var(--primary-ink)]" />
                    <span>{new Date(openDose.dose.scheduled_at).toLocaleString()}</span>
                    {openDose.dose.pre_setup && <span className="text-[color:var(--text-muted)]" title="Before course added">*</span>}
                  </div>
                  {openDose.dose.in_taper && openDose.dose.dose_label ? (
                    <div
                      data-testid="dose-dialog-per-day"
                      className="rounded-xl border-2 p-4"
                      style={{
                        borderColor: "var(--primary)",
                        background: "color-mix(in srgb, var(--primary) 8%, transparent)",
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--primary-ink)" }}>
                          {doseDayLabel(openDose.dose)}
                        </span>
                        {openDose.dose.day_offset && (
                          <span
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                            style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}
                          >
                            Day {openDose.dose.day_offset}{openDose.dose.week_number ? ` · Week ${openDose.dose.week_number}` : ""}
                          </span>
                        )}
                      </div>
                      <div className="font-display font-black text-2xl tracking-tight text-[color:var(--text-main)]">
                        {openDose.dose.dose_label}
                      </div>
                      {openDose.rx.instructions && (
                        <div className="text-xs mt-1.5" style={{ color: "var(--text-muted)" }}>
                          {openDose.rx.instructions}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-[color:var(--text-main)]">
                      <span className="font-semibold">{openDose.dose.dose_label || openDose.rx.dosage}</span>
                      {openDose.rx.instructions && <span className="text-[color:var(--text-muted)]"> · {openDose.rx.instructions}</span>}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className={`dose-pill ${doseClass(openDose.dose)}`}>
                      {openDose.dose.status === "auto_taken" ? "Taken before setup" : openDose.dose.status.replace("_", " ")}
                    </span>
                  </div>
                  {isTooEarlyToTake(openDose.dose) && openDose.dose.status === "pending" && (
                    <div
                      data-testid="dose-not-due-yet"
                      className="text-xs rounded-lg px-3 py-2"
                      style={{ background: "var(--surface-2)", color: "var(--text-muted)" }}
                    >
                      Not due yet — you'll be able to mark this taken closer to the scheduled time.
                      You can still <strong>Skip</strong> in advance if you'll be away.
                    </div>
                  )}
                </div>
              )}
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => mark(openDose.dose, "skipped")} data-testid="dose-skip" className="border-[color:var(--border-subtle)]">
                  <XCircle className="h-4 w-4 mr-2 text-[color:var(--accent-ink)]" /> Skip
                </Button>
                <Button
                  onClick={() => mark(openDose.dose, "taken")}
                  data-testid="dose-taken"
                  disabled={isTooEarlyToTake(openDose.dose)}
                  title={isTooEarlyToTake(openDose.dose) ? "This dose isn't due yet" : ""}
                  className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Mark taken
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
